import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/AdminContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import { Plus, Pencil, Trash2, Search, Upload, Loader2, X, Image, CloudUpload, RefreshCw, ChevronLeft, ChevronRight, Eye, Star, BarChart2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import RichTextEditor from '../ui/RichTextEditor';

const LABEL_POSITIONS = [
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
];

// Image Uploader Component - Beautiful Gallery Style
const ImageUploader = ({ images, onImagesChange, API, getAuthHeaders }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append('files', file));

      const token = localStorage.getItem('rani_admin_token');
      const response = await fetch(`${API}/upload/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // NOTE: Do NOT set Content-Type for FormData - browser sets it with boundary
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Upload failed');
      }
      
      const data = await response.json();
      const newUrls = data.uploaded.map(item => item.url);
      onImagesChange([...images, ...newUrls]);
      
      if (data.errors && data.errors.length > 0) {
        toast.error(`${data.errors.length} files failed to upload`);
      } else {
        toast.success(`${newUrls.length} images uploaded`);
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.message || 'Failed to upload images');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleUrlAdd = () => {
    if (!urlInput.trim()) return;
    const urls = urlInput.split('\n').filter(u => u.trim().startsWith('http'));
    onImagesChange([...images, ...urls]);
    setUrlInput('');
    toast.success(`${urls.length} image URLs added`);
  };

  const handleRemoveImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onImagesChange(newImages);
  };

  const handleMoveImage = (index, direction) => {
    const newImages = [...images];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newImages.length) return;
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    onImagesChange(newImages);
  };

  const setAsMain = (index) => {
    if (index === 0) return;
    const newImages = [...images];
    const [moved] = newImages.splice(index, 1);
    newImages.unshift(moved);
    onImagesChange(newImages);
    toast.success('Image set as main');
  };

  const getImageUrl = (url) => {
    if (url.startsWith('/api')) {
      return `${API.replace('/api', '')}${url}`;
    }
    return url;
  };

  return (
    <div className="space-y-6">
      {/* Beautiful Gallery Preview */}
      {images.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">Product Gallery ({images.length} images)</Label>
          </div>
          
          {/* Main Image + Thumbnails Layout */}
          <div className="grid grid-cols-12 gap-4">
            {/* Main Image (larger) */}
            <div className="col-span-6 relative group">
              <div className="aspect-square rounded-xl overflow-hidden bg-gray-100 shadow-lg border-2 border-primary">
                <img
                  src={getImageUrl(images[0])}
                  alt="Main product image"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/400?text=Image+Error'; }}
                />
                <div className="absolute top-3 left-3 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full shadow">
                  MAIN IMAGE
                </div>
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={() => handleRemoveImage(0)}
                  >
                    <X className="h-4 w-4 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            </div>

            {/* Thumbnail Grid */}
            <div className="col-span-6">
              <div className="grid grid-cols-3 gap-2">
                {images.slice(1).map((url, index) => (
                  <div key={index + 1} className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border hover:border-primary transition-colors">
                    <img
                      src={getImageUrl(url)}
                      alt={`Product image ${index + 2}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Error'; }}
                    />
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 p-1">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        className="h-6 text-xs w-full"
                        onClick={() => setAsMain(index + 1)}
                      >
                        Set Main
                      </Button>
                      <div className="flex gap-1">
                        {index > 0 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="h-6 w-6 p-0"
                            onClick={() => handleMoveImage(index + 1, -1)}
                          >
                            ←
                          </Button>
                        )}
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="h-6 w-6 p-0"
                          onClick={() => handleRemoveImage(index + 1)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                        {index < images.length - 2 && (
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="h-6 w-6 p-0"
                            onClick={() => handleMoveImage(index + 1, 1)}
                          >
                            →
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Add More Images Placeholder */}
                <label 
                  htmlFor="image-upload-more" 
                  className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-primary flex flex-col items-center justify-center cursor-pointer transition-colors bg-gray-50 hover:bg-gray-100"
                >
                  <Plus className="h-6 w-6 text-gray-400" />
                  <span className="text-xs text-gray-500 mt-1">Add more</span>
                  <input
                    type="file"
                    id="image-upload-more"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload Section - Only show prominently if no images */}
      <div 
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
          dragOver 
            ? 'border-primary bg-primary/5' 
            : images.length === 0 
              ? 'border-gray-300 hover:border-primary bg-gradient-to-br from-gray-50 to-white' 
              : 'border-gray-200 hover:border-gray-300 bg-gray-50'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleFileUpload(e.target.files)}
          className="hidden"
          id="image-upload"
        />
        <label htmlFor="image-upload" className="cursor-pointer block">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
            dragOver ? 'bg-primary/20' : 'bg-gray-100'
          }`}>
            <CloudUpload className={`h-8 w-8 ${dragOver ? 'text-primary' : 'text-gray-400'}`} />
          </div>
          <p className="text-base font-medium text-gray-700">
            {images.length === 0 ? 'Drag & drop images here or click to upload' : 'Add more images'}
          </p>
          <p className="text-sm text-muted-foreground mt-1">PNG, JPG, WEBP up to 5MB each</p>
        </label>
        {uploading && (
          <div className="mt-4 flex items-center justify-center gap-2 text-primary">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="font-medium">Uploading...</span>
          </div>
        )}
      </div>

      {/* URL Input - Collapsible */}
      <details className="group">
        <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground flex items-center gap-2">
          <span>Or add images by URL</span>
          <svg className="h-4 w-4 transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </summary>
        <div className="mt-3 flex gap-2">
          <Textarea
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg"
            rows={2}
            className="flex-1"
          />
          <Button type="button" variant="outline" onClick={handleUrlAdd}>
            Add
          </Button>
        </div>
      </details>
    </div>
  );
};

export const AdminProducts = () => {
  const { apiCall, getAuthHeaders, API } = useAdmin();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const categoryFilter = searchParams.get('category');
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [quickEditId, setQuickEditId] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest'); // newest, name_asc, name_desc, order_asc, order_desc
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({
    category_ids: [],
    prices_ron: '',
    prices_eur: '',
    stock: '',
    discount_percent: '',
    is_active: 'no_change',
    is_featured: 'no_change',
  });
  const itemsPerPage = 20;

  const [quickEditData, setQuickEditData] = useState({
    name: { ro: '', en: '' },
    sku: '',
    stock: 0,
    prices: { ron: 0, eur: 0 },
    is_active: true,
    is_featured: false,
  });

  const [formData, setFormData] = useState({
    name: { ro: '', en: '' },
    short_description: { ro: '', en: '' },
    description: { ro: '', en: '' },
    technical_specs: { ro: '', en: '' },
    category_id: '',
    category_ids: [],
    prices: { ron: 0, eur: 0 },
    images: [],
    images_as_colors: false,
    stock: 0,
    sku: '',
    sizes: [],
    colors: [],
    is_active: true,
    is_featured: false,
    discount_percent: 0,
    tags: [],
    labels: [],
    meta_title: { ro: '', en: '' },
    meta_description: { ro: '', en: '' },
    meta_keywords: { ro: '', en: '' },
  });

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [categoryFilter, currentPage]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `/admin/products?page=${currentPage}&limit=${itemsPerPage}`;
      if (categoryFilter) {
        url += `&category=${categoryFilter}`;
      }
      const data = await apiCall(url);
      setProducts(data.products || []);
      setTotalProducts(data.total || 0);
    } catch (err) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await apiCall('/admin/categories');
      setCategories(data || []);
    } catch (err) {
      console.error('Failed to fetch categories');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await apiCall(`/admin/products/${editingProduct.id}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
        toast.success('Product updated');
      } else {
        await apiCall('/admin/products', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
        toast.success('Product created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    try {
      await apiCall(`/admin/products/${deleteConfirmId}`, { method: 'DELETE' });
      toast.success('Product deleted');
      fetchProducts();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || { ro: '', en: '' },
      short_description: product.short_description || { ro: '', en: '' },
      description: product.description || { ro: '', en: '' },
      technical_specs: product.technical_specs || { ro: '', en: '' },
      category_id: product.category_id || '',
      category_ids: product.category_ids || [],
      prices: product.prices || { ron: 0, eur: 0 },
      images: product.images || [],
      images_as_colors: product.images_as_colors || false,
      stock: product.stock || 0,
      sku: product.sku || '',
      sizes: product.sizes || [],
      colors: product.colors || [],
      is_active: product.is_active ?? true,
      is_featured: product.is_featured ?? false,
      discount_percent: product.discount_percent || 0,
      tags: product.tags || [],
      labels: product.labels || [],
      meta_title: product.meta_title || { ro: '', en: '' },
      meta_description: product.meta_description || { ro: '', bg: '', en: '' },
      meta_keywords: product.meta_keywords || { ro: '', bg: '', en: '' },
    });
    setIsDialogOpen(true);
  };

  const handleQuickEdit = (product) => {
    setQuickEditId(product.id);
    setQuickEditData({
      name: product.name || { ro: '', en: '' },
      sku: product.sku || '',
      stock: product.stock || 0,
      prices: product.prices || { ron: 0, eur: 0 },
      is_active: product.is_active ?? true,
      is_featured: product.is_featured ?? false,
    });
  };

  const handleQuickEditSave = async (id) => {
    try {
      await apiCall(`/admin/products/${id}`, {
        method: 'PUT',
        body: JSON.stringify(quickEditData),
      });
      toast.success('Product updated');
      setQuickEditId(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const handleBulkAction = async () => {
    if (!bulkAction || selectedProducts.length === 0) return;
    
    if (bulkAction === 'bulk_edit') {
      setIsBulkEditOpen(true);
      return;
    }
    
    if (bulkAction === 'delete') {
      setBulkDeleteOpen(true);
      return;
    }
    
    try {
      if (bulkAction === 'activate') {
        await Promise.all(selectedProducts.map(id => 
          apiCall(`/admin/products/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: true }) })
        ));
        toast.success('Products activated');
      } else if (bulkAction === 'deactivate') {
        await Promise.all(selectedProducts.map(id => 
          apiCall(`/admin/products/${id}`, { method: 'PUT', body: JSON.stringify({ is_active: false }) })
        ));
        toast.success('Products deactivated');
      } else if (bulkAction === 'feature') {
        await Promise.all(selectedProducts.map(id => 
          apiCall(`/admin/products/${id}`, { method: 'PUT', body: JSON.stringify({ is_featured: true }) })
        ));
        toast.success('Products marked as featured');
      } else if (bulkAction === 'unfeature') {
        await Promise.all(selectedProducts.map(id => 
          apiCall(`/admin/products/${id}`, { method: 'PUT', body: JSON.stringify({ is_featured: false }) })
        ));
        toast.success('Products removed from featured');
      }
      setSelectedProducts([]);
      setBulkAction('');
      fetchProducts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      await Promise.all(selectedProducts.map(id => 
        apiCall(`/admin/products/${id}`, { method: 'DELETE' })
      ));
      toast.success(`${selectedProducts.length} products deleted`);
      setSelectedProducts([]);
      setBulkAction('');
      fetchProducts();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBulkDeleteOpen(false);
    }
  };

  const handleBulkEditApply = async () => {
    if (selectedProducts.length === 0) return;
    
    try {
      const updateData = {};
      
      // Only include fields that have been changed
      if (bulkEditData.category_ids.length > 0) {
        updateData.category_ids = bulkEditData.category_ids;
      }
      if (bulkEditData.prices_ron !== '') {
        updateData.prices = { ...updateData.prices, ron: parseFloat(bulkEditData.prices_ron) };
      }
      if (bulkEditData.prices_eur !== '') {
        updateData.prices = { ...updateData.prices, eur: parseFloat(bulkEditData.prices_eur) };
      }
      if (bulkEditData.stock !== '') {
        updateData.stock = parseInt(bulkEditData.stock);
      }
      if (bulkEditData.discount_percent !== '') {
        updateData.discount_percent = parseFloat(bulkEditData.discount_percent);
      }
      if (bulkEditData.is_active !== 'no_change') {
        updateData.is_active = bulkEditData.is_active === 'true';
      }
      if (bulkEditData.is_featured !== 'no_change') {
        updateData.is_featured = bulkEditData.is_featured === 'true';
      }
      
      if (Object.keys(updateData).length === 0) {
        toast.error('No changes to apply');
        return;
      }
      
      await Promise.all(selectedProducts.map(id => 
        apiCall(`/admin/products/${id}`, { method: 'PUT', body: JSON.stringify(updateData) })
      ));
      
      toast.success(`${selectedProducts.length} products updated`);
      setIsBulkEditOpen(false);
      setBulkEditData({
        category_ids: [],
        prices_ron: '',
        prices_eur: '',
        stock: '',
        discount_percent: '',
        is_active: 'no_change',
        is_featured: 'no_change',
      });
      setSelectedProducts([]);
      setBulkAction('');
      fetchProducts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const toggleBulkCategory = (categoryId) => {
    setBulkEditData(prev => {
      const current = prev.category_ids || [];
      if (current.includes(categoryId)) {
        return { ...prev, category_ids: current.filter(id => id !== categoryId) };
      } else {
        return { ...prev, category_ids: [...current, categoryId] };
      }
    });
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? (cat.name?.ro || cat.name?.en || 'Unknown') : '';
  };

  const getCategoryNames = (categoryIds) => {
    if (!categoryIds || categoryIds.length === 0) return '';
    return categoryIds.map(id => getCategoryName(id)).filter(Boolean).join(', ');
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData({
      name: { ro: '', en: '' },
      short_description: { ro: '', en: '' },
      description: { ro: '', en: '' },
      technical_specs: { ro: '', en: '' },
      category_id: '',
      category_ids: [],
      prices: { ron: 0, eur: 0 },
      images: [],
      images_as_colors: false,
      stock: 0,
      sku: '',
      sizes: [],
      colors: [],
      is_active: true,
      is_featured: false,
      discount_percent: 0,
      tags: [],
      labels: [],
      meta_title: { ro: '', en: '' },
      meta_description: { ro: '', en: '' },
      meta_keywords: { ro: '', en: '' },
    });
  };

  const addLabel = () => {
    setFormData({
      ...formData,
      labels: [...formData.labels, { text: { ro: '', bg: '', en: '' }, image_url: '', position: 'top-right', is_active: true }]
    });
  };

  const removeLabel = (index) => {
    const newLabels = [...formData.labels];
    newLabels.splice(index, 1);
    setFormData({ ...formData, labels: newLabels });
  };

  const updateLabel = (index, field, value) => {
    const newLabels = [...formData.labels];
    if (field.includes('.')) {
      const [mainField, subField] = field.split('.');
      newLabels[index] = { ...newLabels[index], [mainField]: { ...newLabels[index][mainField], [subField]: value } };
    } else {
      newLabels[index] = { ...newLabels[index], [field]: value };
    }
    setFormData({ ...formData, labels: newLabels });
  };

  const toggleCategory = (categoryId) => {
    const currentIds = formData.category_ids || [];
    if (currentIds.includes(categoryId)) {
      setFormData({ ...formData, category_ids: currentIds.filter(id => id !== categoryId) });
    } else {
      setFormData({ ...formData, category_ids: [...currentIds, categoryId] });
    }
  };

  const handleImportWordpress = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('rani_admin_token');
      const res = await fetch(`${API}/admin/products/import-wordpress`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!res.ok) throw new Error('Import failed');
      const data = await res.json();
      toast.success(`Imported ${data.imported} products`);
      fetchProducts();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filteredProducts = products.filter((p) => {
    const matchesSearch = !searchQuery || 
      p.name?.en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.name?.ro?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStock = stockFilter === 'all' || 
      (stockFilter === 'instock' && p.stock > 0) ||
      (stockFilter === 'outofstock' && p.stock <= 0);
    
    return matchesSearch && matchesStock;
  });

  // Sort filtered products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'name_asc':
        return (a.name?.ro || a.name?.en || '').localeCompare(b.name?.ro || b.name?.en || '');
      case 'name_desc':
        return (b.name?.ro || b.name?.en || '').localeCompare(a.name?.ro || a.name?.en || '');
      case 'order_asc':
        return (a.sort_order || 0) - (b.sort_order || 0);
      case 'order_desc':
        return (b.sort_order || 0) - (a.sort_order || 0);
      case 'oldest':
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      case 'newest':
      default:
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    }
  });

  const totalPages = Math.ceil(totalProducts / itemsPerPage);
  const currentCategoryName = categoryFilter ? getCategoryName(categoryFilter) : null;

  return (
    <div className="space-y-6" data-testid="admin-products">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-heading font-bold text-[#1A1625]">
            Products
            {currentCategoryName && (
              <span className="text-primary ml-2">- {currentCategoryName}</span>
            )}
          </h2>
          <p className="text-muted-foreground">
            {categoryFilter ? (
              <button onClick={() => navigate('/rani-admin/products')} className="text-primary hover:underline">
                ← Back to all products
              </button>
            ) : (
              'Manage your store products'
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            id="wordpress-import"
            accept=".json"
            className="hidden"
            onChange={handleImportWordpress}
          />
          <Button
            variant="outline"
            onClick={() => document.getElementById('wordpress-import').click()}
          >
            <Upload className="mr-2 h-4 w-4" /> Import
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-primary" data-testid="add-product-btn">
                <Plus className="mr-2 h-4 w-4" /> Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="basic">Basic Info</TabsTrigger>
                    <TabsTrigger value="images">Images</TabsTrigger>
                    <TabsTrigger value="categories">Categories</TabsTrigger>
                    <TabsTrigger value="labels">Labels</TabsTrigger>
                    <TabsTrigger value="seo">SEO</TabsTrigger>
                  </TabsList>

                  {/* Basic Info Tab */}
                  <TabsContent value="basic" className="space-y-4 mt-4">
                    {/* Names */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Name (RO)</Label>
                        <Input
                          value={formData.name.ro}
                          onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ro: e.target.value } })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Name (EN)</Label>
                        <Input
                          value={formData.name.en}
                          onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                        />
                      </div>
                    </div>

                    {/* Description - All Languages */}
                    <div className="space-y-3">
                      <Label className="text-base font-medium">Short Description (visible at top)</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-muted-foreground">Romanian (RO)</Label>
                          <Textarea
                            value={formData.short_description?.ro || ''}
                            onChange={(e) => setFormData({ ...formData, short_description: { ...formData.short_description, ro: e.target.value } })}
                            rows={2}
                            placeholder="Descriere scurtă..."
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">English (EN)</Label>
                          <Textarea
                            value={formData.short_description?.en || ''}
                            onChange={(e) => setFormData({ ...formData, short_description: { ...formData.short_description, en: e.target.value } })}
                            rows={2}
                            placeholder="Short description..."
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="text-base font-medium">Full Description (below gallery)</Label>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs text-muted-foreground mb-2 block">Romanian (RO)</Label>
                          <RichTextEditor
                            value={formData.description.ro}
                            onChange={(html) => setFormData({ ...formData, description: { ...formData.description, ro: html } })}
                            placeholder="Descriere produs în română..."
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-2 block">English (EN)</Label>
                          <RichTextEditor
                            value={formData.description.en}
                            onChange={(html) => setFormData({ ...formData, description: { ...formData.description, en: html } })}
                            placeholder="Product description in English..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Technical Specifications - with table support */}
                    <div className="space-y-3">
                      <Label className="text-base font-medium">Specificații Tehnice / Technical Specs (Tab 1)</Label>
                      <p className="text-xs text-muted-foreground">Use the table button to create specification tables. If empty, the full description will be used.</p>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs text-muted-foreground mb-2 block">Romanian (RO)</Label>
                          <RichTextEditor
                            value={formData.technical_specs?.ro || ''}
                            onChange={(html) => setFormData({ ...formData, technical_specs: { ...formData.technical_specs, ro: html } })}
                            placeholder="Adaugă specificații tehnice cu tabel..."
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground mb-2 block">English (EN)</Label>
                          <RichTextEditor
                            value={formData.technical_specs?.en || ''}
                            onChange={(html) => setFormData({ ...formData, technical_specs: { ...formData.technical_specs, en: html } })}
                            placeholder="Add technical specifications with table..."
                          />
                        </div>
                      </div>
                    </div>

                    {/* Prices */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Price (RON)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.prices.ron}
                          onChange={(e) => setFormData({ ...formData, prices: { ...formData.prices, ron: parseFloat(e.target.value) || 0 } })}
                          required
                        />
                      </div>
                      <div>
                        <Label>Price (EUR)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.prices.eur}
                          onChange={(e) => setFormData({ ...formData, prices: { ...formData.prices, eur: parseFloat(e.target.value) || 0 } })}
                          required
                        />
                      </div>
                    </div>

                    {/* Stock & SKU */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>În Stoc (Stock Available)</Label>
                        <div className="flex items-center gap-4 mt-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="stock_status"
                              checked={formData.stock > 0}
                              onChange={() => setFormData({ ...formData, stock: 100 })}
                              className="w-4 h-4 text-primary"
                            />
                            <span className="text-sm font-medium text-green-600">Da (Yes)</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="stock_status"
                              checked={formData.stock <= 0}
                              onChange={() => setFormData({ ...formData, stock: 0 })}
                              className="w-4 h-4 text-primary"
                            />
                            <span className="text-sm font-medium text-red-500">Nu (No)</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <Label>SKU (Cod Produs)</Label>
                        <Input
                          value={formData.sku}
                          onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* Sizes */}
                    <div>
                      <Label>Sizes (comma separated, leave empty if no sizes)</Label>
                      <Input
                        value={(formData.sizes || []).join(', ')}
                        onChange={(e) => setFormData({ ...formData, sizes: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                        placeholder="S, M, L, XL or 140x200, 160x200, 180x200"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Leave empty if product has no size options</p>
                    </div>

                    {/* Discount */}
                    <div>
                      <Label>Discount %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.discount_percent}
                        onChange={(e) => setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    {/* Toggles */}
                    <div className="flex gap-8">
                      <label className="flex items-center gap-2">
                        <Switch
                          checked={formData.is_active}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                        />
                        <span>Active</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <Switch
                          checked={formData.is_featured}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                        />
                        <span>Featured</span>
                      </label>
                    </div>
                  </TabsContent>

                  {/* Images Tab */}
                  <TabsContent value="images" className="space-y-4 mt-4">
                    <ImageUploader
                      images={formData.images}
                      onImagesChange={(images) => setFormData({ ...formData, images })}
                      API={API}
                      getAuthHeaders={getAuthHeaders}
                    />
                    
                    {/* Images as Colors Option */}
                    {formData.images.length > 1 && (
                      <div className="border-t pt-4 mt-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <Switch
                            checked={formData.images_as_colors || false}
                            onCheckedChange={(checked) => setFormData({ ...formData, images_as_colors: checked })}
                          />
                          <div>
                            <span className="font-medium">Show images as color variants</span>
                            <p className="text-xs text-muted-foreground">
                              When enabled, each image will be displayed as a different color option for customers to select
                            </p>
                          </div>
                        </label>
                      </div>
                    )}
                  </TabsContent>

                  {/* Categories Tab */}
                  <TabsContent value="categories" className="space-y-4 mt-4">
                    <div>
                      <Label className="mb-3 block">Select Categories (multiple allowed)</Label>
                      <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border rounded-lg p-3">
                        {categories.map((cat) => (
                          <label key={cat.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(formData.category_ids || []).includes(cat.id)}
                              onChange={() => toggleCategory(cat.id)}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">{cat.name?.en || cat.name?.ro}</span>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Selected: {(formData.category_ids || []).length} categories
                      </p>
                    </div>

                    {/* Legacy single category (for backward compatibility) */}
                    <div>
                      <Label>Primary Category (legacy)</Label>
                      <Select
                        value={formData.category_id || "none"}
                        onValueChange={(v) => setFormData({ ...formData, category_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select primary category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.name?.en || cat.name?.ro}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TabsContent>

                  {/* Labels Tab */}
                  <TabsContent value="labels" className="space-y-4 mt-4">
                    <p className="text-sm text-muted-foreground">
                      Add custom labels/badges to product images (e.g., "New", "Best Seller", "Limited Edition")
                    </p>
                    
                    {(formData.labels || []).map((label, index) => (
                      <div key={index} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Label {index + 1}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-500"
                            onClick={() => removeLabel(index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Position</Label>
                            <Select
                              value={label.position || 'top-right'}
                              onValueChange={(v) => updateLabel(index, 'position', v)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {LABEL_POSITIONS.map((pos) => (
                                  <SelectItem key={pos.value} value={pos.value}>{pos.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Image URL (optional)</Label>
                            <Input
                              placeholder="https://..."
                              value={label.image_url || ''}
                              onChange={(e) => updateLabel(index, 'image_url', e.target.value)}
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2">
                          <div>
                            <Label>Text (RO)</Label>
                            <Input
                              placeholder="Nou"
                              value={label.text?.ro || ''}
                              onChange={(e) => updateLabel(index, 'text.ro', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Text (BG)</Label>
                            <Input
                              placeholder="Ново"
                              value={label.text?.bg || ''}
                              onChange={(e) => updateLabel(index, 'text.bg', e.target.value)}
                            />
                          </div>
                          <div>
                            <Label>Text (EN)</Label>
                            <Input
                              placeholder="New"
                              value={label.text?.en || ''}
                              onChange={(e) => updateLabel(index, 'text.en', e.target.value)}
                            />
                          </div>
                        </div>

                        <label className="flex items-center gap-2">
                          <Switch
                            checked={label.is_active !== false}
                            onCheckedChange={(checked) => updateLabel(index, 'is_active', checked)}
                          />
                          <span className="text-sm">Active</span>
                        </label>
                      </div>
                    ))}
                    
                    <Button type="button" variant="outline" onClick={addLabel}>
                      <Plus className="h-4 w-4 mr-2" /> Add Label
                    </Button>
                  </TabsContent>

                  {/* SEO Tab */}
                  <TabsContent value="seo" className="space-y-4 mt-4">
                    <p className="text-sm text-muted-foreground">
                      SEO meta tags for search engine optimization
                    </p>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Meta Title (RO)</Label>
                        <Input
                          value={formData.meta_title?.ro || ''}
                          onChange={(e) => setFormData({ ...formData, meta_title: { ...formData.meta_title, ro: e.target.value } })}
                        />
                      </div>
                      <div>
                        <Label>Meta Title (BG)</Label>
                        <Input
                          value={formData.meta_title?.bg || ''}
                          onChange={(e) => setFormData({ ...formData, meta_title: { ...formData.meta_title, bg: e.target.value } })}
                        />
                      </div>
                      <div>
                        <Label>Meta Title (EN)</Label>
                        <Input
                          value={formData.meta_title?.en || ''}
                          onChange={(e) => setFormData({ ...formData, meta_title: { ...formData.meta_title, en: e.target.value } })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Meta Description (RO)</Label>
                        <Textarea
                          rows={2}
                          value={formData.meta_description?.ro || ''}
                          onChange={(e) => setFormData({ ...formData, meta_description: { ...formData.meta_description, ro: e.target.value } })}
                        />
                      </div>
                      <div>
                        <Label>Meta Description (BG)</Label>
                        <Textarea
                          rows={2}
                          value={formData.meta_description?.bg || ''}
                          onChange={(e) => setFormData({ ...formData, meta_description: { ...formData.meta_description, bg: e.target.value } })}
                        />
                      </div>
                      <div>
                        <Label>Meta Description (EN)</Label>
                        <Textarea
                          rows={2}
                          value={formData.meta_description?.en || ''}
                          onChange={(e) => setFormData({ ...formData, meta_description: { ...formData.meta_description, en: e.target.value } })}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Meta Keywords (RO)</Label>
                        <Input
                          placeholder="lenjerie, pat, bumbac"
                          value={formData.meta_keywords?.ro || ''}
                          onChange={(e) => setFormData({ ...formData, meta_keywords: { ...formData.meta_keywords, ro: e.target.value } })}
                        />
                      </div>
                      <div>
                        <Label>Meta Keywords (BG)</Label>
                        <Input
                          placeholder="спално бельо, памук"
                          value={formData.meta_keywords?.bg || ''}
                          onChange={(e) => setFormData({ ...formData, meta_keywords: { ...formData.meta_keywords, bg: e.target.value } })}
                        />
                      </div>
                      <div>
                        <Label>Meta Keywords (EN)</Label>
                        <Input
                          placeholder="bedding, cotton, sheets"
                          value={formData.meta_keywords?.en || ''}
                          onChange={(e) => setFormData({ ...formData, meta_keywords: { ...formData.meta_keywords, en: e.target.value } })}
                        />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-primary">
                    {editingProduct ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Bulk Actions and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={bulkAction} onValueChange={setBulkAction}>
            <SelectTrigger className="w-36">
              <SelectValue placeholder="Bulk actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bulk_edit">Bulk Edit</SelectItem>
              <SelectItem value="delete">Delete</SelectItem>
              <SelectItem value="activate">Publish</SelectItem>
              <SelectItem value="deactivate">Draft</SelectItem>
              <SelectItem value="feature">Mark Featured</SelectItem>
              <SelectItem value="unfeature">Unmark Featured</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleBulkAction} disabled={!bulkAction || selectedProducts.length === 0}>
            Apply
          </Button>

          <Select value={categoryFilter || 'all'} onValueChange={(v) => navigate(v === 'all' ? '/rani-admin/products' : `/rani-admin/products?category=${v}`)}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Select a category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name?.ro || cat.name?.en}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={stockFilter} onValueChange={setStockFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Stock status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stock status</SelectItem>
              <SelectItem value="instock">In stock</SelectItem>
              <SelectItem value="outofstock">Out of stock</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="name_asc">Name A-Z</SelectItem>
              <SelectItem value="name_desc">Name Z-A</SelectItem>
              <SelectItem value="order_asc">Order ↑</SelectItem>
              <SelectItem value="order_desc">Order ↓</SelectItem>
            </SelectContent>
          </Select>

          <span className="text-sm text-muted-foreground">
            {totalProducts} items
          </span>
        </div>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 w-64"
          />
        </div>
      </div>

      {/* Products Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            </div>
          ) : sortedProducts.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No products found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="py-3 px-4 w-10">
                      <Checkbox
                        checked={selectedProducts.length === sortedProducts.length && sortedProducts.length > 0}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedProducts(sortedProducts.map(p => p.id));
                          } else {
                            setSelectedProducts([]);
                          }
                        }}
                      />
                    </th>
                    <th className="text-left py-3 px-4 w-12">Image</th>
                    <th className="text-left py-3 px-4">Name</th>
                    <th className="text-left py-3 px-4">SKU</th>
                    <th className="text-left py-3 px-4">Stock</th>
                    <th className="text-left py-3 px-4">Price</th>
                    <th className="text-left py-3 px-4">Categories</th>
                    <th className="text-center py-3 px-4">★</th>
                    <th className="text-left py-3 px-4">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProducts.map((product) => (
                    <React.Fragment key={product.id}>
                      <tr className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <Checkbox
                            checked={selectedProducts.includes(product.id)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedProducts([...selectedProducts, product.id]);
                              } else {
                                setSelectedProducts(selectedProducts.filter(id => id !== product.id));
                              }
                            }}
                          />
                        </td>
                        <td className="py-3 px-4">
                          <img
                            src={product.images?.[0] || 'https://via.placeholder.com/40'}
                            alt=""
                            className="w-12 h-12 rounded object-cover"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => handleEdit(product)}
                            className="font-medium text-primary hover:underline text-left"
                          >
                            {product.name?.ro || product.name?.en}
                          </button>
                          <div className="text-xs text-muted-foreground mt-1">
                            ID: {product.id?.slice(0, 8)}
                          </div>
                          <div className="flex gap-2 mt-1 text-xs">
                            <button onClick={() => handleEdit(product)} className="text-primary hover:underline">Edit</button>
                            <span className="text-gray-300">|</span>
                            <button onClick={() => handleQuickEdit(product)} className="text-primary hover:underline">Quick Edit</button>
                            <span className="text-gray-300">|</span>
                            <button type="button" onClick={() => handleDeleteClick(product.id)} className="text-red-500 hover:underline">Trash</button>
                            <span className="text-gray-300">|</span>
                            <a href={`/product/${product.id}`} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">View</a>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono text-xs">{product.sku || '-'}</td>
                        <td className="py-3 px-4">
                          <span className={product.stock > 0 ? 'text-green-600 font-medium' : 'text-red-500 font-medium'}>
                            {product.stock > 0 ? 'In stock' : 'Out of stock'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="font-medium">{product.prices?.ron?.toFixed(0)} lei</span>
                          {product.discount_percent > 0 && (
                            <span className="ml-1 text-red-500 text-xs">-{product.discount_percent}%</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-xs text-muted-foreground max-w-xs truncate">
                          {getCategoryNames(product.category_ids) || getCategoryName(product.category_id) || '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {product.is_featured ? (
                            <Star className="h-4 w-4 text-gold fill-gold mx-auto" />
                          ) : (
                            <Star className="h-4 w-4 text-gray-300 mx-auto" />
                          )}
                        </td>
                        <td className="py-3 px-4 text-xs">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            product.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {product.is_active ? 'Published' : 'Draft'}
                          </span>
                          <div className="text-muted-foreground mt-1 space-y-0.5">
                            <div title="Created">
                              📅 {product.created_at ? new Date(product.created_at).toLocaleDateString() : '-'}
                            </div>
                            {product.updated_at && product.updated_at !== product.created_at && (
                              <div title="Modified" className="text-blue-500">
                                ✏️ {new Date(product.updated_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>

                      {/* Quick Edit Row */}
                      {quickEditId === product.id && (
                        <tr className="bg-blue-50 border-b">
                          <td colSpan={9} className="p-4">
                            <div className="flex flex-wrap gap-4 items-end">
                              <div>
                                <Label className="text-xs">Name (RO)</Label>
                                <Input
                                  value={quickEditData.name.ro}
                                  onChange={(e) => setQuickEditData({ ...quickEditData, name: { ...quickEditData.name, ro: e.target.value } })}
                                  className="w-44 h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">SKU</Label>
                                <Input
                                  value={quickEditData.sku}
                                  onChange={(e) => setQuickEditData({ ...quickEditData, sku: e.target.value })}
                                  className="w-24 h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Stock</Label>
                                <div className="flex gap-2">
                                  <label className="flex items-center gap-1 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`quick_stock_${product.id}`}
                                      checked={quickEditData.stock > 0}
                                      onChange={() => setQuickEditData({ ...quickEditData, stock: 100 })}
                                      className="w-3 h-3"
                                    />
                                    <span className="text-xs text-green-600">Da</span>
                                  </label>
                                  <label className="flex items-center gap-1 cursor-pointer">
                                    <input
                                      type="radio"
                                      name={`quick_stock_${product.id}`}
                                      checked={quickEditData.stock <= 0}
                                      onChange={() => setQuickEditData({ ...quickEditData, stock: 0 })}
                                      className="w-3 h-3"
                                    />
                                    <span className="text-xs text-red-500">Nu</span>
                                  </label>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Price (RON)</Label>
                                <Input
                                  type="number"
                                  value={quickEditData.prices.ron}
                                  onChange={(e) => setQuickEditData({ ...quickEditData, prices: { ...quickEditData.prices, ron: parseFloat(e.target.value) || 0 } })}
                                  className="w-24 h-8"
                                />
                              </div>
                              <label className="flex items-center gap-2">
                                <Checkbox
                                  checked={quickEditData.is_active}
                                  onCheckedChange={(checked) => setQuickEditData({ ...quickEditData, is_active: checked })}
                                />
                                <span className="text-xs">Published</span>
                              </label>
                              <label className="flex items-center gap-2">
                                <Checkbox
                                  checked={quickEditData.is_featured}
                                  onCheckedChange={(checked) => setQuickEditData({ ...quickEditData, is_featured: checked })}
                                />
                                <span className="text-xs">Featured</span>
                              </label>
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => handleQuickEditSave(product.id)} className="bg-primary h-8">
                                  Update
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setQuickEditId(null)} className="h-8">
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              «
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              »
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Edit Dialog */}
      <Dialog open={isBulkEditOpen} onOpenChange={setIsBulkEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Edit - {selectedProducts.length} Products Selected</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-4">
            {/* Left Column - Selected Products */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Selected Products</Label>
              <div className="border rounded-lg max-h-60 overflow-y-auto p-2 bg-gray-50">
                {filteredProducts
                  .filter(p => selectedProducts.includes(p.id))
                  .map(product => (
                    <div key={product.id} className="flex items-center gap-2 py-1 text-sm">
                      <X 
                        className="h-3 w-3 text-red-500 cursor-pointer hover:text-red-700" 
                        onClick={() => setSelectedProducts(selectedProducts.filter(id => id !== product.id))}
                      />
                      <span className="truncate">{product.name?.ro || product.name?.en}</span>
                    </div>
                  ))}
              </div>

              {/* Categories Checkboxes */}
              <div className="mt-4">
                <Label className="text-sm font-medium mb-2 block">Categories</Label>
                <div className="border rounded-lg max-h-48 overflow-y-auto p-2 bg-gray-50 space-y-1">
                  {categories.map(cat => (
                    <label key={cat.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 p-1 rounded">
                      <Checkbox
                        checked={(bulkEditData.category_ids || []).includes(cat.id)}
                        onCheckedChange={() => toggleBulkCategory(cat.id)}
                      />
                      <span className="text-sm">{cat.name?.ro || cat.name?.en}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Edit Fields */}
            <div className="space-y-4">
              <div>
                <Label>Price (RON)</Label>
                <Select 
                  value={bulkEditData.prices_ron === '' ? 'no_change' : 'custom'}
                  onValueChange={(v) => setBulkEditData({ ...bulkEditData, prices_ron: v === 'no_change' ? '' : bulkEditData.prices_ron })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="— No change —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_change">— No change —</SelectItem>
                    <SelectItem value="custom">Set custom price</SelectItem>
                  </SelectContent>
                </Select>
                {bulkEditData.prices_ron !== '' && (
                  <Input
                    type="number"
                    placeholder="Enter price in RON"
                    value={bulkEditData.prices_ron}
                    onChange={(e) => setBulkEditData({ ...bulkEditData, prices_ron: e.target.value })}
                    className="mt-2"
                  />
                )}
              </div>

              <div>
                <Label>Discount %</Label>
                <Select 
                  value={bulkEditData.discount_percent === '' ? 'no_change' : 'custom'}
                  onValueChange={(v) => setBulkEditData({ ...bulkEditData, discount_percent: v === 'no_change' ? '' : bulkEditData.discount_percent })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="— No change —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_change">— No change —</SelectItem>
                    <SelectItem value="custom">Set discount</SelectItem>
                  </SelectContent>
                </Select>
                {bulkEditData.discount_percent !== '' && (
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="Enter discount percentage"
                    value={bulkEditData.discount_percent}
                    onChange={(e) => setBulkEditData({ ...bulkEditData, discount_percent: e.target.value })}
                    className="mt-2"
                  />
                )}
              </div>

              <div>
                <Label>Stock</Label>
                <Select 
                  value={bulkEditData.stock === '' ? 'no_change' : 'custom'}
                  onValueChange={(v) => setBulkEditData({ ...bulkEditData, stock: v === 'no_change' ? '' : bulkEditData.stock })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="— No change —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_change">— No change —</SelectItem>
                    <SelectItem value="custom">Set stock</SelectItem>
                  </SelectContent>
                </Select>
                {bulkEditData.stock !== '' && (
                  <Input
                    type="number"
                    min="0"
                    placeholder="Enter stock quantity"
                    value={bulkEditData.stock}
                    onChange={(e) => setBulkEditData({ ...bulkEditData, stock: e.target.value })}
                    className="mt-2"
                  />
                )}
              </div>

              <div>
                <Label>Status</Label>
                <Select 
                  value={bulkEditData.is_active}
                  onValueChange={(v) => setBulkEditData({ ...bulkEditData, is_active: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="— No change —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_change">— No change —</SelectItem>
                    <SelectItem value="true">Published</SelectItem>
                    <SelectItem value="false">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Featured</Label>
                <Select 
                  value={bulkEditData.is_featured}
                  onValueChange={(v) => setBulkEditData({ ...bulkEditData, is_featured: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="— No change —" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_change">— No change —</SelectItem>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setIsBulkEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkEditApply} className="bg-primary">
              Update {selectedProducts.length} Products
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Product
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this product? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button variant="outline" onClick={handleDeleteCancel}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete {selectedProducts.length} Products
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete these products? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDeleteConfirm}>
              Delete All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
